/**
 * Tools Explorer Component
 * Browse and explore available MCP tools with descriptions
 */

import React, { useState, useMemo } from 'react';
import { Input, Spin, Empty } from 'antd';
import { SearchOutlined, ToolOutlined } from '@ant-design/icons';
import { useMCPTools } from '@/hooks/useMCP';
import './ToolsExplorer.css';

interface ToolsExplorerProps {
  onToolSelect?: (toolName: string, toolDescription: string) => void;
}

export const ToolsExplorer: React.FC<ToolsExplorerProps> = ({
  onToolSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: toolsData, isLoading } = useMCPTools();

  // Categorize tools
  const categorizedTools = useMemo(() => {
    if (!toolsData?.tools) return {};

    const categories: Record<string, any[]> = {
      Kanban: [],
      Tickets: [],
      Leads: [],
      'Business Partners': [],
      Calendar: [],
      Other: [],
    };

    toolsData.tools.forEach((tool) => {
      const name = tool.name.toLowerCase();
      if (name.includes('kanban') || name.includes('board')) {
        categories['Kanban'].push(tool);
      } else if (name.includes('ticket')) {
        categories['Tickets'].push(tool);
      } else if (name.includes('lead')) {
        categories['Leads'].push(tool);
      } else if (name.includes('bp') || name.includes('business_partner')) {
        categories['Business Partners'].push(tool);
      } else if (name.includes('calendar') || name.includes('event')) {
        categories['Calendar'].push(tool);
      } else {
        categories['Other'].push(tool);
      }
    });

    // Remove empty categories
    Object.keys(categories).forEach((key) => {
      if (categories[key].length === 0) {
        delete categories[key];
      }
    });

    return categories;
  }, [toolsData]);

  // Filter tools based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categorizedTools;

    const filtered: Record<string, any[]> = {};
    const term = searchTerm.toLowerCase();

    Object.entries(categorizedTools).forEach(([category, tools]) => {
      const matchingTools = tools.filter(
        (tool) =>
          tool.name.toLowerCase().includes(term) ||
          tool.description?.toLowerCase().includes(term)
      );

      if (matchingTools.length > 0) {
        filtered[category] = matchingTools;
      }
    });

    return filtered;
  }, [categorizedTools, searchTerm]);

  const handleToolClick = (tool: any) => {
    if (onToolSelect) {
      onToolSelect(tool.name, tool.description || '');
    }
  };

  if (isLoading) {
    return (
      <div className="tools-explorer-loading">
        <Spin size="large" />
        <p>Loading available tools...</p>
      </div>
    );
  }

  const totalTools = Object.values(filteredCategories).reduce(
    (sum, tools) => sum + tools.length,
    0
  );

  return (
    <div className="tools-explorer">
      <div className="tools-explorer-header">
        <h3>
          <ToolOutlined /> Available Tools
        </h3>
        <p className="tools-count">{totalTools} tools available</p>
      </div>

      <div className="tools-search">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search tools..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
      </div>

      <div className="tools-categories">
        {totalTools === 0 ? (
          <Empty description="No tools found" />
        ) : (
          Object.entries(filteredCategories).map(([category, tools]) => (
            <div key={category} className="tools-category">
              <h4 className="category-title">
                {category} ({tools.length})
              </h4>

              <div className="tools-list">
                {tools.map((tool) => (
                  <div
                    key={tool.name}
                    className="tool-card"
                    onClick={() => handleToolClick(tool)}
                  >
                    <div className="tool-header">
                      <span className="tool-name">{tool.name}</span>
                    </div>

                    {tool.description && (
                      <p className="tool-description">{tool.description}</p>
                    )}

                    {tool.inputSchema?.properties && (
                      <div className="tool-params">
                        <span className="params-label">Parameters:</span>
                        <div className="params-list">
                          {Object.keys(tool.inputSchema.properties).map(
                            (param) => (
                              <span key={param} className="param-badge">
                                {param}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
